import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";

import type { FeelingAtHomeWidgetSuggestionDto } from "@/api/types";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { Lightning02Icon } from "@/components/Icons/Lightning02Icon";
import { buttonVariants } from "@/design-system/Button";
import { Divider } from "@/design-system/Divider";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { Tooltip } from "@/design-system/Tooltip";
import { formatDistance } from "@/helpers/date";
import { canListAnySurvey } from "@/helpers/permissions";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { analyticsMutations } from "@/queries/analytics/mutations";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { AnalyticsWidgetCard } from "./analytics/AnalyticsWidgetCard";
import { HomeFahSuggestionWidgetDetailsModal } from "./HomeFahSuggestionWidgetDetailsModal";

const FAH_SUGGESTION_LAST_READ_AT = "fah-suggestion-last-read-at";
const FALLBACK_DATA: FeelingAtHomeWidgetSuggestionDto[] = [
  {
    id: "1",
    title: "-",
    description: "-",
    tips: [],
  },
  {
    id: "2",
    title: "-",
    description: "-",
    tips: [],
  },
  {
    id: "3",
    title: "-",
    description: "-",
    tips: [],
  },
] satisfies FeelingAtHomeWidgetSuggestionDto[];

export function HomeFahFollowUpSuggestionWidget(): React.ReactNode {
  const clockDate = useCurrentDate();
  const [isDetailsModalOpened, detailsModalHandler] = useBool(false);
  const [activeSuggestionId, setActiveSuggestionId] = useState<string | undefined>(undefined);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const hasPermission = usePermission();
  const { t } = useTranslation();
  const slug = useSlug();
  const analyticsQueries = useAnalyticsQueries();
  const {
    data: fahSuggestions,
    isPending: isPendingFahSuggestions,
    isError: isErrorFahSuggestions,
    error: errorFahSuggestions,
  } = useQuery(analyticsQueries.fahSuggestions());
  const updateFahSuggestionReadMutation = analyticsMutations.useUpdateFahSuggestionRead();
  const handleOpenSuggestion = (suggestion: FeelingAtHomeWidgetSuggestionDto) => {
    setActiveSuggestionId(suggestion.id);
    detailsModalHandler.setTrue();
    if (!suggestion.viewedAt) {
      updateFahSuggestionReadMutation.mutate(suggestion.id);
    }
  };
  const isError = isErrorFahSuggestions;
  const isPending = isPendingFahSuggestions;
  const error = errorFahSuggestions;

  // Highlight widget when there are new suggestions
  useEffect(() => {
    if (!fahSuggestions?.lastUpdatedAt || !fahSuggestions?.suggestions) {
      return;
    }

    const lastReadAt: string | undefined = getLocalStorageValue(FAH_SUGGESTION_LAST_READ_AT, undefined);
    if (!lastReadAt || new Date(fahSuggestions.lastUpdatedAt) > new Date(lastReadAt)) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setIsHighlighted(true);
    }

    // Update the last read date to now
    updateLocalStorage(FAH_SUGGESTION_LAST_READ_AT, new Date().toISOString());
  }, [fahSuggestions?.lastUpdatedAt, fahSuggestions?.suggestions]);

  // If BE returns `undefined` instead of an empty array, this implies that the project does not yet have no FAH data.
  // Therefore we hide the widget
  if (!isError && !isPending && !fahSuggestions.suggestions) {
    return null;
  }

  // This indicates whether there are any suggestions available when the project SHOULD have sufficient FAH data.
  const isDataAvailable = !!fahSuggestions?.suggestions && fahSuggestions.suggestions.length > 0;
  const suggestions = isDataAvailable ? fahSuggestions.suggestions! : FALLBACK_DATA;

  return (
    <>
      <AnalyticsWidgetCard.Root
        title={t("component.analytics-widget.fah-suggestions.title")}
        description={t("component.analytics-widget.fah-suggestions.description")}
        isError={isError}
        error={error}
        isLoading={isPending}
        isDataAvailable={isDataAvailable}
        href={isDataAvailable && hasPermission(canListAnySurvey) ? routes.surveys.fah({ slug }) : undefined}
        isHighlighted={isHighlighted}
      >
        <AnalyticsWidgetCard.Body>
          <Tiles columns={1} gap="sm">
            {suggestions?.map((suggestion) => (
              <button
                type="button"
                onClick={() => handleOpenSuggestion(suggestion)}
                key={suggestion.id}
                className={twResolve(
                  buttonVariants({ variant: "soft" }),
                  "flex h-fit w-full items-center justify-between gap-space-md border-transparent p-space-md whitespace-normal",
                  suggestion.viewedAt && "border-border-default bg-background-default hover:bg-background-brand-light",
                )}
              >
                <span className="flex flex-1 flex-col gap-space-sm">
                  <span className="text-left text-heading-4">{suggestion.title}</span>
                  <span className="text-left text-caption">{suggestion.description}</span>
                </span>
                <ChevronRightIcon tone="secondary" size={24} />
              </button>
            ))}
          </Tiles>
        </AnalyticsWidgetCard.Body>
        <AnalyticsWidgetCard.Footer>
          <Stack gap="sm">
            <Divider />
            <aside className="flex w-full flex-wrap items-center justify-between gap-space-sm text-text-secondary">
              <Inline gap="xs" align="center">
                <Lightning02Icon />
                <p>{t("common.label.generated-by-ai")}</p>
              </Inline>
              {fahSuggestions?.lastUpdatedAt && (
                <Tooltip tooltip={t("component.analytics-widget.fah-suggestions.tooltip.last-updated")}>
                  <time className="whitespace-nowrap">
                    {t("common.label.last-updated", {
                      distance: formatDistance(t, {
                        start: new Date(fahSuggestions.lastUpdatedAt),
                        end: clockDate,
                      }),
                    })}
                  </time>
                </Tooltip>
              )}
            </aside>
          </Stack>
        </AnalyticsWidgetCard.Footer>
      </AnalyticsWidgetCard.Root>
      <HomeFahSuggestionWidgetDetailsModal
        suggestion={suggestions.find((currSuggestion) => currSuggestion.id === activeSuggestionId)}
        isOpened={isDetailsModalOpened && Boolean(activeSuggestionId)}
        onOpenChange={detailsModalHandler.set}
      />
    </>
  );
}
